import { ChangeDetectionStrategy, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { LuxonDateAdapter, MAT_LUXON_DATE_FORMATS } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTimepickerModule, MatTimepickerOption } from '@angular/material/timepicker';

import { TranslocoModule } from '@jsverse/transloco';
import { DateTime } from 'luxon';

import { CapitalizePipe } from '@hmax-order/frontend/utils';
import { ErrorMessage } from '../../../interfaces';
import { FirstErrorMessagePipe } from '../../pipes';

@Component({
  selector: 'hmax-input-time',
  standalone: true,
  templateUrl: './input-time.component.html',
  styleUrls: ['./input-time.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    CapitalizePipe,
    FirstErrorMessagePipe,
    MatFormFieldModule,
    MatInputModule,
    MatTimepickerModule,
    ReactiveFormsModule,
    TranslocoModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: DateAdapter,
      useClass: LuxonDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: MAT_LUXON_DATE_FORMATS,
    },
  ],
})
export class InputTimeComponent implements OnInit {
  @Input() label!: string;
  @Input() placeholder!: string;
  @Input() formGroup!: FormGroup;
  @Input() controlName!: string;
  @Input() appearance: 'outline' | 'fill' = 'outline';
  /** Evita reserva fixa de espaço abaixo do campo quando não há erro/hint */
  @Input() subscriptSizing: 'fixed' | 'dynamic' = 'fixed';
  /** Passo entre as opções oferecidas pelo `mat-timepicker`, ex.: '15m', '30m', '1h'. */
  @Input() interval = '15m';
  /**
   * Campo só de seleção, que é o default: o `mat-timepicker` aceita texto livre no input, então sem
   * isto dava para digitar `eewq` em cima de um campo de hora. Espelha o `disableManualInput` do
   * `hmax-input-date`, mas ligado, porque hora tem lista curta e o picker cobre todos os casos.
   */
  @Input() disableManualInput = true;
  /**
   * Horário mínimo e máximo oferecidos pelo picker. Aceitam `null` porque quem calcula o limite
   * costuma devolver "sem limite" como `null`, e `strictTemplates` recusaria isso num input opcional.
   * O valor do controle continua sendo um `DateTime`.
   */
  @Input() min?: DateTime | null;
  @Input() max?: DateTime | null;
  /**
   * Lista explícita de horários oferecidos. Quando informada, substitui `interval`, `min` e `max` — é
   * o caminho para janela de disponibilidade DESCONTÍNUA, que `min`/`max` não conseguem expressar.
   */
  @Input() options?: readonly MatTimepickerOption<DateTime>[] | null;

  errorMessages: ErrorMessage[] = [
    { error: 'required', message: 'required-field' },
    { error: 'matTimepickerParse', message: 'invalid-time' },
    { error: 'matTimepickerMin', message: 'min-time' },
    { error: 'matTimepickerMax', message: 'max-time' },
  ];

  get formControl(): AbstractControl {
    return this.formGroup.controls[this.controlName];
  }

  ngOnInit(): void {
    // Carregar controlName com o label se não houver controlName definido
    this.controlName = this.controlName ?? this.label;
  }
}
